import type { useUnistyles } from "react-native-unistyles";
import type { TestSignalPath } from "@visioni/a3s-monitoring/app";

type Theme = ReturnType<typeof useUnistyles>["theme"];

/**
 * A signal path's colour, shared by its send button, its log rows' stripe, the
 * legend and the summary, so an operator can match a row to the button that
 * produced it at a glance.
 *
 * From the theme rather than a fixed shade: no one shade both carries white
 * text and stands out against the dark cards — see `testSignalPaths` in
 * `@visioni/a3s-ui`'s theme. Deliberately not green, amber or red, which
 * already mean outcome on this page.
 */
/**
 * How strongly a path's panel is washed with its colour. Faint enough to stay
 * behind the text, but enough that two adjacent rows of different paths read
 * as different panels and not just different stripes. Stronger in dark mode,
 * where the same wash over a dark card barely registers.
 */
const TINT_ALPHA = { light: 0.05, dark: 0.1 } as const;

export function signalPathColors(path: TestSignalPath, theme: Theme): { fill: string; onFill: string; tint: string } {
  const { fill, onFill } = theme.colors.testSignalPaths[path];
  return { fill, onFill, tint: withAlpha(fill, TINT_ALPHA[theme.currentTheme]) };
}

/** `#RRGGBB` at the given opacity. */
function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(alpha)})`;
}
